/**
 * Shared Recharts pieces for the Explore Line + Bar views (POS2-883): the series
 * legend, the per-point tooltip, the daily axis tick, and the ChartContainer config.
 * Both views render the same engine pivot, so these stay identical between them.
 */
import { format, parseISO } from "date-fns";

import type { ChartConfig } from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import type { ExploreWindow, Granularity } from "@/lib/explore-window";
import type { Measure, Split } from "@/hooks/use-explore-state";
import { GRAN_UNIT_KEYS, type ExplorePivot } from "@/lib/explore-engine";
import { useTranslation } from "@/i18n/use-translation";
import { dateFnsLocale, formatDate, formatPercent } from "@/lib/format";
import {
  formatBucketLong,
  formatBucketTick,
  type ChartSeries,
  type TickSpan,
} from "@/lib/explore-chart";

export type ExploreViewProps = {
  pivot: ExplorePivot;
  /** The preceding-window pivot — seeds the first bucket's Δ from the period just before the
   * window (POS2-1038). Consumed by the Table + the Line/Bar tooltip via `cellDeltas`. */
  priorPivot: ExplorePivot | null;
  measure: Measure;
  /** Resolved org→site→kitchen currency (never hardcoded). */
  currency: string;
  gran: Granularity;
  split: Split;
  /** The active [from, to) window — the views densify the pivot across it. */
  window: ExploreWindow;
  /** Resolved site timezone (org→site→kitchen) — buckets are generated in it to
   * line up with the RPC's site-local `time_key`s. */
  timezone: string;
};

/** ChartContainer needs a config; feed it the series labels + colours (the views
 * still pass literal hex straight to stroke/fill, so this is only for the wrapper). */
export function seriesChartConfig(series: ChartSeries[]): ChartConfig {
  return Object.fromEntries(series.map((s) => [s.key, { label: s.label, color: s.color }]));
}

/** Swatch + label per series, below the chart. Rendered only when a split is active;
 * order mirrors the pivot's cost-desc series order. */
export function ExploreLegend({ series }: { series: ChartSeries[] }) {
  return (
    <div
      className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500"
      data-testid="explore-legend"
    >
      {series.map((s) => (
        <span key={s.key} className="flex items-center gap-1.5">
          <span className="size-2.5 shrink-0 rounded-[2px]" style={{ background: s.color }} />
          <span className="max-w-[12rem] truncate" title={s.label}>
            {s.label}
          </span>
        </span>
      ))}
    </div>
  );
}

type TooltipItem = {
  dataKey?: string | number;
  name?: string | number;
  value?: number;
  payload?: { key?: string };
};

/**
 * Per-point tooltip: the date-long label, then one row per series (swatch + split label + formatted
 * measure + the series' ▲/▼ change vs the PREVIOUS period). Deltas come from the shared `cellDeltas`
 * map (keyed `${bucket}|${series}`), so the tooltip agrees with the Table exactly — including the
 * first bucket's baseline from the preceding window (POS2-1038). Passed to Recharts as
 * `content={<ExploreTooltip … />}`; active/payload are injected on clone.
 */
export function ExploreTooltip({
  active,
  payload,
  series,
  gran,
  format: formatValue,
  deltas,
}: {
  active?: boolean;
  payload?: TooltipItem[];
  series: ChartSeries[];
  gran: Granularity;
  format: (n: number) => string;
  /** Per-`${bucket}|${series}` Δ from `cellDeltas` — same numbers as the Table, partial-edge
   * suppression and the first-bucket baseline included. */
  deltas: Map<string, number | null>;
}) {
  const { locale, t } = useTranslation();
  if (!active || !payload?.length) return null;
  const key = String(payload[0]?.payload?.key ?? "");
  const byKey = new Map(series.map((s) => [s.key, s]));
  const single = series.length === 1;
  // Drop segments that are 0 in this bucket — a "0 kg" row is noise (POS2-1032).
  const items = payload.filter((item) => Number(item.value ?? 0) !== 0);
  if (!items.length) return null;
  return (
    <div className="grid min-w-[9rem] gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs shadow-lg">
      <div className="font-medium text-gray-900">{formatBucketLong(key, gran, { locale, t })}</div>
      <div className="grid gap-1">
        {items.map((item) => {
          const seriesKey = String(item.dataKey);
          const s = byKey.get(seriesKey);
          const delta = deltas.get(`${key}|${seriesKey}`) ?? null;
          return (
            // Fixed value + delta columns (label/spacer flexes) so weights line up across
            // rows, whether or not a row has a delta (POS2-1032).
            <div key={seriesKey} className="flex items-center gap-3 leading-none">
              {single ? (
                <span className="flex-1" />
              ) : (
                <span className="flex min-w-0 flex-1 items-center gap-1.5 text-gray-500">
                  <span
                    className="size-2 shrink-0 rounded-[2px]"
                    style={{ background: s?.color ?? "#9ca3af" }}
                  />
                  <span className="truncate">{s?.label ?? String(item.name)}</span>
                </span>
              )}
              <span className="shrink-0 text-right font-medium tabular-nums text-gray-900">
                {formatValue(Number(item.value ?? 0))}
              </span>
              <span
                // Waste up = bad (matches the panel pill). min-w forms a column; nowrap keeps the
                // arrow on the same line as the % for large deltas (POS2-1038).
                className={cn(
                  "min-w-[4rem] shrink-0 whitespace-nowrap text-right font-semibold tabular-nums",
                  delta != null && (delta > 0 ? "text-red-600" : "text-emerald-600"),
                )}
                title={
                  delta != null
                    ? t("waste.vsPrevious", { unit: t(GRAN_UNIT_KEYS[gran]) })
                    : undefined
                }
              >
                {delta != null
                  ? `${delta > 0 ? "▲" : "▼"} ${formatPercent(locale, Math.abs(delta))}`
                  : ""}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Daily axis tick: weekday initial over day-of-month, placed inside the axis so
 * Recharts positions each under its bucket. Weekend days are muted (the shading band
 * carries the main distinction).
 */
const DAY_ONLY: Intl.DateTimeFormatOptions = { day: "numeric" };

export function BucketTick({
  x = 0,
  y = 0,
  payload,
  span,
}: {
  x?: number;
  y?: number;
  payload?: { value: string };
  span: TickSpan;
}) {
  const { locale, t } = useTranslation();
  if (!payload) return null;
  const d = parseISO(payload.value);

  // Past a single month the axis is sampled every nth day, so the weekday initial says nothing
  // and a bare day number is unplaceable — name the month, and the year once the window crosses
  // one (POS2-1068). One line, matching the weekly and monthly axes.
  if (span !== "month") {
    const label = formatBucketTick(payload.value, "daily", span, { locale, t });
    return (
      <g transform={`translate(${x},${y})`} textAnchor="middle">
        <text y={16} fontSize={11} fill="#374151">
          {label}
        </text>
      </g>
    );
  }

  // The two-letter weekday initial has no `Intl` field set — `weekday: "short"` is three letters
  // in English — so it stays a date-fns token, still locale-aware (POS2-1134 CP4b).
  const initial = format(d, "EEEEEE", { locale: dateFnsLocale(locale) });
  const dayNumber = formatDate(locale, d, DAY_ONLY);
  const weekend = d.getDay() === 0 || d.getDay() === 6;
  return (
    <g transform={`translate(${x},${y})`} textAnchor="middle">
      <text y={16} fontSize={11} fontWeight={600} fill={weekend ? "#9ca3af" : "#6b7280"}>
        {initial}
      </text>
      <text y={29} fontSize={11} fill={weekend ? "#9ca3af" : "#374151"}>
        {dayNumber}
      </text>
    </g>
  );
}
